import React, { useRef, useCallback, useState, useEffect } from "react";
import { ZoomKeyframe, ZOOM_PRESETS } from "../../../types";
import { FPS } from "../../../../../constants";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
} from "../../ui/context-menu";

interface ZoomTimelineProps {
  keyframes: ZoomKeyframe[];
  overlayDuration: number; // in frames
  currentFrame: number; // relative to overlay start
  onKeyframeUpdate: (keyframeId: string, updates: Partial<ZoomKeyframe>) => void;
  onAddKeyframe: (startFrame: number, endFrame: number, scale?: number) => void;
  onDeleteKeyframe: (keyframeId: string) => void;
  onSelectKeyframe: (keyframeId: string | null) => void;
  selectedKeyframeId: string | null;
}

const SCALE_COLORS: Record<string, string> = {
  default: "bg-gray-400",
  shallow: "bg-blue-500",
  moderate: "bg-purple-500",
  maximum: "bg-pink-500",
  custom: "bg-orange-500",
};

const PRESET_OPTIONS = [
  { key: "shallow", label: "Shallow (1.5x)", scale: 1.5, color: "bg-blue-500" },
  { key: "moderate", label: "Moderate (2x)", scale: 2, color: "bg-purple-500" },
  { key: "maximum", label: "Deep (3x)", scale: 3, color: "bg-pink-500" },
];

const getScalePreset = (scale: number): string => {
  for (const [key, value] of Object.entries(ZOOM_PRESETS)) {
    if (Math.abs(value - scale) < 0.01) return key;
  }
  return "custom";
};

const formatTime = (frames: number): string => {
  const seconds = frames / FPS;
  return `${seconds.toFixed(1)}s`;
};

/**
 * Visual timeline showing zoom keyframes as draggable/resizable blocks.
 * Features context menu for quick zoom creation with presets.
 */
export const ZoomTimeline: React.FC<ZoomTimelineProps> = ({
  keyframes,
  overlayDuration,
  currentFrame,
  onKeyframeUpdate,
  onAddKeyframe,
  onDeleteKeyframe,
  onSelectKeyframe,
  selectedKeyframeId,
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [contextMenuFrame, setContextMenuFrame] = useState<number | null>(null);
  const [draggingState, setDraggingState] = useState<{
    keyframeId: string;
    mode: "move" | "resize-left" | "resize-right";
    startX: number;
    startFrame: number;
    endFrame: number;
  } | null>(null);

  // Generate timestamp markers
  const timestampInterval = Math.max(FPS, Math.ceil(overlayDuration / 10 / FPS) * FPS);
  const timestamps: number[] = [];
  for (let t = 0; t <= overlayDuration; t += timestampInterval) {
    timestamps.push(t);
  }

  // Calculate frame from mouse position
  const getFrameFromMouseEvent = useCallback(
    (e: React.MouseEvent | MouseEvent): number => {
      if (!timelineRef.current) return 0;
      const rect = timelineRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickPercent = Math.max(0, Math.min(1, clickX / rect.width));
      return Math.round(clickPercent * overlayDuration);
    },
    [overlayDuration]
  );

  // Check if frame position is available (not overlapping with keyframes)
  const isFrameAvailable = useCallback(
    (frame: number): boolean => {
      return !keyframes.some(
        kf => frame >= kf.startFrame && frame <= kf.endFrame
      );
    },
    [keyframes]
  );

  // Handle context menu open
  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      const frame = getFrameFromMouseEvent(e);
      setContextMenuFrame(frame);
    },
    [getFrameFromMouseEvent]
  );

  // Add zoom with preset from context menu
  const handleAddZoomPreset = useCallback(
    (scale: number) => {
      if (contextMenuFrame === null) return;

      // Check if position is available
      if (!isFrameAvailable(contextMenuFrame)) {
        console.warn("Cannot add zoom: position overlaps with existing keyframe");
        return;
      }

      // Default duration: 2 seconds
      const defaultDuration = Math.min(FPS * 2, overlayDuration - contextMenuFrame);
      if (defaultDuration <= 0) return;

      onAddKeyframe(contextMenuFrame, contextMenuFrame + defaultDuration, scale);
      setContextMenuFrame(null);
    },
    [contextMenuFrame, isFrameAvailable, overlayDuration, onAddKeyframe]
  );

  // Handle keyframe block mouse down
  const handleKeyframeMouseDown = useCallback(
    (e: React.MouseEvent, keyframe: ZoomKeyframe, mode: "move" | "resize-left" | "resize-right") => {
      e.preventDefault();
      e.stopPropagation();

      onSelectKeyframe(keyframe.id);

      setDraggingState({
        keyframeId: keyframe.id,
        mode,
        startX: e.clientX,
        startFrame: keyframe.startFrame,
        endFrame: keyframe.endFrame,
      });
    },
    [onSelectKeyframe]
  );

  // Handle mouse move during drag
  useEffect(() => {
    if (!draggingState) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!timelineRef.current) return;

      const rect = timelineRef.current.getBoundingClientRect();
      const deltaX = e.clientX - draggingState.startX;
      const deltaPercent = deltaX / rect.width;
      const deltaFrames = Math.round(deltaPercent * overlayDuration);

      const keyframe = keyframes.find(kf => kf.id === draggingState.keyframeId);
      if (!keyframe) return;

      let newStartFrame = draggingState.startFrame;
      let newEndFrame = draggingState.endFrame;

      if (draggingState.mode === "move") {
        newStartFrame = Math.max(0, draggingState.startFrame + deltaFrames);
        const duration = draggingState.endFrame - draggingState.startFrame;
        newEndFrame = newStartFrame + duration;

        if (newEndFrame > overlayDuration) {
          newEndFrame = overlayDuration;
          newStartFrame = newEndFrame - duration;
        }
      } else if (draggingState.mode === "resize-left") {
        newStartFrame = Math.max(0, Math.min(draggingState.startFrame + deltaFrames, draggingState.endFrame - FPS));
      } else if (draggingState.mode === "resize-right") {
        newEndFrame = Math.min(overlayDuration, Math.max(draggingState.endFrame + deltaFrames, draggingState.startFrame + FPS));
      }

      // Check for overlaps
      const wouldOverlap = keyframes.some(kf => {
        if (kf.id === draggingState.keyframeId) return false;
        return !(newEndFrame <= kf.startFrame || newStartFrame >= kf.endFrame);
      });

      if (!wouldOverlap) {
        onKeyframeUpdate(draggingState.keyframeId, {
          startFrame: newStartFrame,
          endFrame: newEndFrame,
        });
      }
    };

    const handleMouseUp = () => {
      setDraggingState(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [draggingState, keyframes, overlayDuration, onKeyframeUpdate]);

  const canAddAtContextMenu = contextMenuFrame !== null && isFrameAvailable(contextMenuFrame);

  return (
    <div className="space-y-1">
      {/* Timestamp labels */}
      <div className="relative h-4 text-[10px] text-muted-foreground">
        {timestamps.map((frame) => (
          <div
            key={frame}
            className="absolute transform -translate-x-1/2"
            style={{ left: `${(frame / overlayDuration) * 100}%` }}
          >
            {formatTime(frame)}
          </div>
        ))}
      </div>

      {/* Timeline track with context menu */}
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            ref={timelineRef}
            onContextMenu={handleContextMenu}
            className="relative h-14 bg-muted/50 rounded-md cursor-crosshair overflow-hidden border"
          >
            {/* Timestamp markers */}
            {timestamps.map((frame) => (
              <div
                key={`marker-${frame}`}
                className="absolute top-0 bottom-0 w-px bg-border"
                style={{ left: `${(frame / overlayDuration) * 100}%` }}
              />
            ))}

            {/* Zoom keyframe blocks */}
            {keyframes.map((keyframe) => {
              const left = (keyframe.startFrame / overlayDuration) * 100;
              const width = ((keyframe.endFrame - keyframe.startFrame) / overlayDuration) * 100;
              const preset = getScalePreset(keyframe.scale);
              const colorClass = SCALE_COLORS[preset] || SCALE_COLORS.custom;
              const isSelected = selectedKeyframeId === keyframe.id;
              const isDragging = draggingState?.keyframeId === keyframe.id;

              return (
                <div
                  key={keyframe.id}
                  className={`absolute top-1 bottom-1 ${colorClass} rounded-sm transition-all ${
                    isSelected ? "ring-2 ring-primary ring-offset-1" : ""
                  } ${isDragging ? "opacity-80 shadow-lg" : "opacity-90 hover:opacity-100"}`}
                  style={{
                    left: `${left}%`,
                    width: `${Math.max(width, 2)}%`,
                    minWidth: "24px",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectKeyframe(keyframe.id);
                  }}
                >
                  {/* Left resize handle */}
                  <div
                    className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 transition-colors z-10 rounded-l-sm"
                    onMouseDown={(e) => handleKeyframeMouseDown(e, keyframe, "resize-left")}
                  />

                  {/* Center drag area */}
                  <div
                    className="absolute inset-0 cursor-move flex items-center justify-center"
                    style={{ left: "8px", right: "8px" }}
                    onMouseDown={(e) => handleKeyframeMouseDown(e, keyframe, "move")}
                  >
                    <span className="text-[10px] font-semibold text-white truncate px-1 select-none drop-shadow-sm">
                      {keyframe.scale.toFixed(1)}x
                    </span>
                  </div>

                  {/* Right resize handle */}
                  <div
                    className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 transition-colors z-10 rounded-r-sm"
                    onMouseDown={(e) => handleKeyframeMouseDown(e, keyframe, "resize-right")}
                  />
                </div>
              );
            })}

            {/* Playhead */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-primary pointer-events-none z-20"
              style={{ left: `${(currentFrame / overlayDuration) * 100}%` }}
            >
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2.5 h-2.5 bg-primary rounded-full shadow-md" />
            </div>

            {/* Empty state hint */}
            {keyframes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground pointer-events-none">
                Right-click to add zoom effect
              </div>
            )}
          </div>
        </ContextMenuTrigger>

        <ContextMenuContent className="w-48">
          {canAddAtContextMenu ? (
            <>
              <ContextMenuSub>
                <ContextMenuSubTrigger>
                  <span>Add Zoom at {formatTime(contextMenuFrame ?? 0)}</span>
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="w-40">
                  {PRESET_OPTIONS.map((preset) => (
                    <ContextMenuItem
                      key={preset.key}
                      onClick={() => handleAddZoomPreset(preset.scale)}
                    >
                      <div className={`w-3 h-3 rounded-sm ${preset.color} mr-2`} />
                      {preset.label}
                    </ContextMenuItem>
                  ))}
                </ContextMenuSubContent>
              </ContextMenuSub>
            </>
          ) : (
            <ContextMenuItem disabled>
              Position overlaps existing zoom
            </ContextMenuItem>
          )}

          {selectedKeyframeId && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem
                onClick={() => onDeleteKeyframe(selectedKeyframeId)}
                className="text-destructive"
              >
                Delete Selected Zoom
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground pt-1">
        {PRESET_OPTIONS.map((preset) => (
          <div key={preset.key} className="flex items-center gap-1">
            <div className={`w-3 h-2 ${preset.color} rounded-sm`} />
            <span>{preset.label.split(" ")[0]}</span>
          </div>
        ))}
        <div className="flex items-center gap-1">
          <div className="w-3 h-2 bg-orange-500 rounded-sm" />
          <span>Custom</span>
        </div>
      </div>
    </div>
  );
};
